/**
 * formatBaht — single source-of-truth Thai baht formatter.
 *
 * Defaults: locale='th' (uses 'th-TH' BCP-47 tag), decimals=2, withSymbol=true.
 *
 * Behavior:
 * - null / undefined / NaN → '—' (em dash, signals missing value)
 * - 0 → '฿0.00' (with default options)
 * - positive → '฿5,000.00' (thousands grouped, 2 decimals — standard THB format)
 * - negative → '-฿100.00' (minus BEFORE symbol, per Thai banking convention)
 * - { decimals: 0 } → '฿5,000' (use when whole-baht display is preferred)
 * - { locale: 'en' } → uses 'en-US' for Western digits
 * - { withSymbol: false } → '5,000.00' (no ฿ prefix, for PDF or composed strings)
 *
 * Decimals default is 2 — standard banking/accounting format for THB. Callers
 * wanting whole-baht display (e.g., unit price tags) pass { decimals: 0 }
 * explicitly. Auto-inference based on Number.isInteger is intentionally avoided
 * to prevent silent decimal-flip on rounding artefacts.
 *
 * Do NOT use this in PDF generators (web/src/lib/{invoice,taxInvoice,receipt,}pdfGenerator.ts).
 * They intentionally pass locale=undefined to pdfme's canvas renderer to avoid Thai-script
 * digit issues. Those files keep their inline formatters.
 */

export type BahtLocale = 'th' | 'en'

export interface FormatBahtOptions {
  locale?: BahtLocale
  decimals?: 0 | 2
  withSymbol?: boolean
}

export function formatBaht(amount: number | null | undefined, opts: FormatBahtOptions = {}): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—'
  const { locale = 'th', decimals = 2, withSymbol = true } = opts
  const bcp47 = locale === 'th' ? 'th-TH' : 'en-US'
  const isNegative = amount < 0
  const abs = Math.abs(amount)
  const formatted = abs.toLocaleString(bcp47, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  const body = withSymbol ? `฿${formatted}` : formatted
  return isNegative ? `-${body}` : body
}
